import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import AuthProvider, { useAuth } from './context/AuthContext';
import ProtectedRoute, { MfaProtectedRoute } from './components/ProtectedRoute';
import Login from './components/Login';
import Register from './components/Register';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import Navbar from './components/common/Navbar';
import Footer from './components/common/Footer';
import Layout from './components/Layout';
import GlobalStyles from './assets/styles/globalStyles';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ThemeProvider } from 'styled-components';
import { theme } from './theme';
import { ReceiptProvider } from './context/ReceiptContext';
import EnhancedChatWidget from './components/EnhancedChatWidget';

const Dashboard = React.lazy(() => import('./components/Dashboard'));
const Products = React.lazy(() => import('./components/ProductsPage'));
const TaxCategoryManagementPage = React.lazy(() => import('./components/TaxCategoryManagementPage'));
const Customers = React.lazy(() => import('./components/CustomersPage'));
const Orders = React.lazy(() => import('./components/OrdersPage'));
const Invoices = React.lazy(() => import('./components/InvoicesPage'));
const Receipts = React.lazy(() => import('./components/ReceiptsPage'));
const Transactions = React.lazy(() => import('./components/TransactionsPage'));
const StockLevels = React.lazy(() => import('./components/StockLevelsPage'));
const Reports = React.lazy(() => import('./components/ReportsPage'));
const UserRoles = React.lazy(() => import('./components/UserRolesPage'));
const UserManagementLayout = React.lazy(() => import('./components/UserManagement/UserManagementLayout'));
const UserListPage = React.lazy(() => import('./components/UserManagement/UserListPage'));
const PermissionsPage = React.lazy(() => import('./components/UserManagement/PermissionsPage'));
const FundSourcesPage = React.lazy(() => import('./components/FundManagement/FundSourcesPage'));
const FundAccountsPage = React.lazy(() => import('./components/FundManagement/FundAccountsPage'));
const FundTransactionsPage = React.lazy(() => import('./components/FundManagement/FundTransactionsPage'));
const FundRetirementsPage = React.lazy(() => import('./components/FundManagement/FundRetirementsPage'));
const FundApprovalsPage = React.lazy(() => import('./components/FundManagement/FundApprovalsPage'));
const FundReportingDashboard = React.lazy(() => import('./components/FundManagement/FundReportingDashboard'));
const AuditComplianceDashboard = React.lazy(() => import('./components/AuditComplianceDashboard'));
const DetailView = React.lazy(() => import('./components/DetailView'));
const UserPreferences = React.lazy(() => import('./components/UserPreferences'));
const ProductDetails = React.lazy(() => import('./components/ProductDetails'));
const MfaSetup = React.lazy(() => import('./components/MfaSetup'));
const TwoFactorSettings = React.lazy(() => import('./components/TwoFactorSettings'));
const AccountSettings = React.lazy(() => import('./pages/AccountSettings'));

// Bakery Module Integration
const BakeryLayout = React.lazy(() => import('./components/bakery/BakeryLayout'));
const BakeryRoutes = React.lazy(() => import('./routes/bakeryRoutes'));

// Photography Module Integration
const PhotographyLayout = React.lazy(() => import('./components/Photography/PhotographyLayout'));
const PhotographyRoutes = React.lazy(() => import('./routes/photographyRoutes'));

const LogisticsLayout = React.lazy(() => import('./components/Logistics/LogisticsLayout'));
const LogisticsRoutes = React.lazy(() => import('./routes/logisticsRoutes'));

// Salon Management Module Integration
const SalonLayout = React.lazy(() => import('./components/Salon/SalonLayout'));
const SalonRoutes = React.lazy(() => import('./routes/salonRoutes'));

const App = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <ThemeProvider theme={theme}>
      <AuthProvider>
        <ReceiptProvider>
          <Router>
            <GlobalStyles />
            <Layout>
              <ToastContainer position="top-right" />
	      <Suspense fallback={<div>Loading...</div>}>
              <Routes>
                {/* Public routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/mfa-setup" element={<MfaSetup />} />
                
                {/* Protected routes */}
                <Route element={<ProtectedRoute />}>
                  {/* Root redirects to dashboard */}
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/account" element={<AccountSettings />} />
                  <Route path="/security/two-factor" element={<TwoFactorSettings />} />
                  <Route path="/products" element={<Products />} />
                  <Route path="/customers" element={<Customers />} />
                  <Route path="/orders" element={<Orders />} />
                  <Route path="/invoices" element={<Invoices />} />
                  <Route path="/receipts" element={<Receipts />} />
                  <Route path="/transactions" element={<Transactions />} />
                  <Route path="/stock-levels" element={<StockLevels />} />
                  <Route path="/reports" element={<Reports />} />
                  <Route path="/user-roles" element={<UserRoles />} />
	                <Route path="/tax-categories" element={<TaxCategoryManagementPage />} />
                  <Route path="/fund-management/accounts" element={<FundAccountsPage />} />
                  <Route path="/user-management/*" element={<UserManagementLayout />}>
                    <Route index element={<UserListPage />} />
                    <Route path="users" element={<UserListPage />} />
                    <Route path="roles" element={<UserRoles />} />
                    <Route path="permissions" element={<PermissionsPage />} />
                  </Route>
                  
                  {/* Bakery Management Module */}
                  <Route path="/bakery/*" element={<BakeryLayout />}>
                    <Route path="*" element={<BakeryRoutes />} />
                  </Route>
	   
	          {/* Photography Management Module */}
                  <Route path="/photography/*" element={<PhotographyLayout />}>
                    <Route path="*" element={<PhotographyRoutes />} />
                  </Route>

	          {/* Logistics Management Module */}
                  <Route path="/logistics/*" element={<LogisticsLayout />}>
                    <Route path="*" element={<LogisticsRoutes />} />
                  </Route>

	          {/* Salon Management Module */}
                  <Route path="/salon/*" element={<SalonLayout />}>
                    <Route path="*" element={<SalonRoutes />} />
                  </Route>
                  
                  <Route path="/preferences" element={<UserPreferences />} />
                  <Route path="/:type/:id" element={<DetailView />} />
                  <Route path="/products/:id" element={<ProductDetails />} />
                  
                  {/* MFA protected routes */}
                  <Route element={<MfaProtectedRoute />}>
                    <Route path="/fund-management/sources" element={<FundSourcesPage />} />
                    <Route path="/fund-management/transactions" element={<FundTransactionsPage />} />
                    <Route path="/fund-management/retirements" element={<FundRetirementsPage />} />
                    <Route path="/fund-management/approvals" element={<FundApprovalsPage />} />
                    <Route path="/fund-management/reporting" element={<FundReportingDashboard />} />
                    <Route path="/audit-compliance" element={<AuditComplianceDashboard />} />
                  </Route>
                </Route>
                
                {/* Catch-all route */}
                <Route path="*" element={<Navigate to={isAuthenticated() ? "/dashboard" : "/login"} replace />} />
              </Routes>
	      </Suspense>
              {isAuthenticated() && <EnhancedChatWidget />}
            </Layout>
          </Router>
        </ReceiptProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
